import { useMonthScheduleStates, useScheduleControls } from '@lp/features/Scheduler/hooks';
import {
  ConflictsDrawerModal,
  HrSystemConflictsDrawerModal,
  SchedulerControlHeader,
  SchedulerLoadingOverlay,
  PublishBlockerTooltip,
} from '@lp/features/Scheduler/components';
import {
  useCCSchedulerData,
  useCCSchedulerMutationActions,
  useCCSchedulerServices,
  useCCSchedulerShiftHighlight,
  useCCScheduleValidation,
  useCCShiftChange,
  useCCUnblockShift,
  useCCWorkdaySync,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/hooks';
import { ccConflictsDrawerData } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/constants';
import {
  CCHrSystemConflictCard,
  CCSchedulerControlRow,
  CCSchedulerTable,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/components';
import { useAuthContext } from '@lp/core/auth';
import { SchedulerPermissions } from '@/permissions';

type SchedulerClinicalCoordinatorsProps = {
  readonly?: boolean;
};

export const SchedulerClinicalCoordinators = ({ readonly }: SchedulerClinicalCoordinatorsProps) => {
  const { startDay, selectedDate, selectedDateRange, daysInterval, onMonthPrev, onMonthNext, onDateChange } =
    useScheduleControls();
  const { shiftsData, isShiftsLoading, monthSchedule, isPlanningMode, togglePlanningMode, planningData } =
    useCCSchedulerData({ startDay, dateRange: selectedDateRange });
  const { userList, resourceShiftTotalsMap } = useCCSchedulerServices(selectedDate, shiftsData);
  const onShiftChange = useCCShiftChange(selectedDateRange, monthSchedule, isPlanningMode);
  const { checkPermission } = useAuthContext();
  const canUnblockShifts = checkPermission(SchedulerPermissions.CCSchedulesFixationTypeWrite);
  const onUnblockShift = useCCUnblockShift(selectedDateRange, monthSchedule, isPlanningMode);
  const workdayData = useCCWorkdaySync(startDay, selectedDateRange, monthSchedule, isPlanningMode, readonly);
  const actions = useCCSchedulerMutationActions(startDay, planningData);
  const validationData = useCCScheduleValidation(planningData, shiftsData, isPlanningMode);
  const { isEditable, isGenerating, isResettable } = useMonthScheduleStates(monthSchedule, isPlanningMode, readonly);
  const { highlightByViolation, highlightResourceEmptyShift } = useCCSchedulerShiftHighlight({
    selectedDate,
    daysInterval,
    shiftsData,
  });
  const showBackdrop = isGenerating || isShiftsLoading;

  if (!shiftsData || !userList) return null;

  return (
    <div className="flex flex-col min-h-full">
      <div className="p-6 pb-2 sticky top-0 z-12 bg-base-70">
        <SchedulerControlHeader
          readonly={readonly}
          settingsPath="/schedule/cc/settings"
          actions={actions}
          monthSchedule={monthSchedule}
          planningData={planningData}
          isPlanningMode={isPlanningMode}
          togglePlanningMode={togglePlanningMode}
          violationsCount={validationData?.count}
          validationIsFetching={validationData?.isFetching}
          publishReadinessData={validationData?.publishReadiness}
          fillingState={validationData?.fillingState}
          conflictsButton={
            <ConflictsDrawerModal
              tabsData={ccConflictsDrawerData}
              validationData={validationData}
              onItemClick={highlightByViolation}
            />
          }
          hrConflictsButton={
            <HrSystemConflictsDrawerModal hrSystemData={workdayData} ConflictCard={CCHrSystemConflictCard} />
          }
          publishBlockerTooltip={
            <PublishBlockerTooltip
              shiftsData={shiftsData}
              publishReadinessData={validationData?.publishReadiness}
              onResourceClick={highlightResourceEmptyShift}
            />
          }
          startDay={startDay}
          selectedDate={selectedDate}
          onMonthPrev={onMonthPrev}
          onMonthNext={onMonthNext}
        >
          <CCSchedulerControlRow
            readonly={readonly}
            selectedDate={selectedDate}
            shiftsData={shiftsData}
            workdayData={workdayData}
            onDateChange={onDateChange}
          />
        </SchedulerControlHeader>
      </div>
      <div className="flex flex-col gap-6 relative p-6 pt-4 h-full">
        {showBackdrop && (
          <SchedulerLoadingOverlay
            isGenerating={isGenerating}
            isMutating={actions.isMutating}
            generationInfo={planningData?.autoSchedulerInfo}
            onGenerationCancel={actions.onGenerationCancel}
          />
        )}

        <div className="flex flex-col pl-3 pt-3 bg-white rounded-large shadow-small">
          <CCSchedulerTable
            selectedDate={selectedDate}
            daysInterval={daysInterval}
            userList={userList}
            shiftsData={shiftsData}
            isEditable={isEditable}
            canResetShifts={isResettable}
            canUnblockShifts={canUnblockShifts}
            shiftTotalsMap={resourceShiftTotalsMap}
            dayViolations={validationData?.byDayMap}
            resourceViolations={validationData?.byResourceMap}
            onShiftChange={onShiftChange}
            onUnblockShift={canUnblockShifts ? onUnblockShift : undefined}
          />
        </div>
      </div>
    </div>
  );
};
